<template>
    <div class="-m-5 md:m-0 h-full flex-1 overflow-hidden">
        <tabulator
            :columns="columns"
            :actions="actions"
            :refresh.sync="refreshTable"
            :loading="tableIsLoading"
            :loading-text="tableLoadingText"
            ajax-path="rhipe/customers"
        />
        <app-modal
            :show.sync="showAddModal"
            width="lg:w-1/3"
            bg="bg-gray-200"
        >
            <template slot="header">Create New Customer</template>
            <template slot="body">
                <div class="card">
                    <div class="card-header text-xs">General Information</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="addForm.customerName"
                            placeholder="Customer Name"
                            :error="addForm.errors.get('customerName')"
                            @updated="addForm.errors.clear('customerName')"
                            focus
                            required
                        />
                        <app-input
                            v-model="addForm.webUrl"
                            placeholder="Customer Website URL"
                            :error="addForm.errors.get('webUrl')"
                            @updated="addForm.errors.clear('webUrl')"
                        />
                        <app-input
                            v-model="addForm.email"
                            placeholder="Email"
                            :error="addForm.errors.get('email')"
                            @updated="addForm.errors.clear('email')"
                        />
                        <app-input
                            v-model="addForm.registrationNumber"
                            placeholder="Business Registration Number (E.g. ABN)"
                            :error="addForm.errors.get('registrationNumber')"
                            @updated="addForm.errors.clear('registrationNumber')"
                        />
                    </div>
                </div>
                <div class="card mt-4">
                    <div class="card-header text-xs">Primary Address</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="addForm.address.street1"
                            placeholder="Street 1"
                            :error="addForm.errors.get('address.street1')"
                            @updated="addForm.errors.clear('address.street1')"
                            required
                        />
                        <app-input
                            v-model="addForm.address.street2"
                            placeholder="Street 2"
                            :error="addForm.errors.get('address.street2')"
                            @updated="addForm.errors.clear('address.street2')"
                        />
                        <app-input
                            v-model="addForm.address.street3"
                            placeholder="Street 3"
                            :error="addForm.errors.get('address.street3')"
                            @updated="addForm.errors.clear('address.street3')"
                        />
                        <app-input
                            v-model="addForm.address.city"
                            placeholder="City"
                            :error="addForm.errors.get('address.city')"
                            @updated="addForm.errors.clear('address.city')"
                            required
                        />
                        <app-input
                            v-model="addForm.address.postcode"
                            placeholder="Post Code"
                            :error="addForm.errors.get('address.postcode')"
                            @updated="addForm.errors.clear('address.postcode')"
                            required
                        />
                        <app-select
                            v-model="addForm.address.state"
                            placeholder="State"
                            :error="addForm.errors.get('address.state')"
                            @updated="addForm.errors.clear('address.state')"
                            :options="statesOptions[addForm.address.country]"
                            value-key="id"
                            label-key="label"
                            required
                        />
                        <app-select
                            v-model="addForm.address.country"
                            placeholder="Country"
                            :error="addForm.errors.get('address.country')"
                            @updated="(value) => {
                                addForm.errors.clear('address.country')
                                addForm.address.state = null;
                                addForm.address.iso2Code = iso2CodesOptions[value]
                            }"
                            :options="countriesOptions"
                            required
                        />
                        <checkbox
                            v-model="addForm.usePrimaryAddressAsBilling"
                        >Use this as the billing address</checkbox>
                    </div>
                </div>
                <div class="card mt-4" v-if="!addForm.usePrimaryAddressAsBilling">
                    <div class="card-header text-xs">Billing Address</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="addForm.billingAddress.street1"
                            placeholder="Street 1"
                            :error="addForm.errors.get('billingAddress.street1')"
                            @updated="addForm.errors.clear('billingAddress.street1')"
                            required
                        />
                        <app-input
                            v-model="addForm.billingAddress.street2"
                            placeholder="Street 2"
                            :error="addForm.errors.get('billingAddress.street2')"
                            @updated="addForm.errors.clear('billingAddress.street2')"
                        />
                        <app-input
                            v-model="addForm.billingAddress.street3"
                            placeholder="Street 3"
                            :error="addForm.errors.get('billingAddress.street3')"
                            @updated="addForm.errors.clear('billingAddress.street3')"
                        />
                        <app-input
                            v-model="addForm.billingAddress.city"
                            placeholder="City"
                            :error="addForm.errors.get('billingAddress.city')"
                            @updated="addForm.errors.clear('billingAddress.city')"
                            required
                        />
                        <app-input
                            v-model="addForm.billingAddress.postcode"
                            placeholder="Post Code"
                            :error="addForm.errors.get('billingAddress.postcode')"
                            @updated="addForm.errors.clear('billingAddress.postcode')"
                            required
                        />
                        <app-select
                            v-model="addForm.billingAddress.state"
                            placeholder="State"
                            :error="addForm.errors.get('billingAddress.state')"
                            @updated="addForm.errors.clear('billingAddress.state')"
                            :options="statesOptions[addForm.billingAddress.country]"
                            value-key="id"
                            label-key="label"
                            required
                        />
                        <app-select
                            v-model="addForm.billingAddress.country"
                            placeholder="Country"
                            :error="addForm.errors.get('billingAddress.country')"
                            @updated="(value) => {
                                addForm.errors.clear('billingAddress.country')
                                addForm.billingAddress.state = null;
                                addForm.billingAddress.iso2Code = iso2CodesOptions[value];
                            }"
                            :options="countriesOptions"
                            required
                        />
                    </div>
                </div>
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddModal = false"
                        :disable="creating"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="create"
                        :loading="creating"
                    >Create</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showEditModal"
            width="lg:w-1/3"
            bg="bg-gray-200"
        >
            <template slot="header">Edit Customer</template>
            <template slot="body">
                <div class="card">
                    <div class="card-header text-xs">General Information</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="editForm.customerName"
                            placeholder="Customer Name"
                            :error="editForm.errors.get('customerName')"
                            @updated="editForm.errors.clear('customerName')"
                            focus
                            required
                        />
                        <app-input
                            v-model="editForm.webUrl"
                            placeholder="Customer Website URL"
                            :error="editForm.errors.get('webUrl')"
                            @updated="editForm.errors.clear('webUrl')"
                        />
                        <app-input
                            v-model="editForm.email"
                            placeholder="Email"
                            :error="editForm.errors.get('email')"
                            @updated="editForm.errors.clear('email')"
                        />
                        <app-input
                            v-model="editForm.registrationNumber"
                            placeholder="Business Registration Number (E.g. ABN)"
                            :error="editForm.errors.get('registrationNumber')"
                            @updated="editForm.errors.clear('registrationNumber')"
                        />
                    </div>
                </div>
                <div class="card mt-4">
                    <div class="card-header text-xs">Primary Address</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="editForm.address.street1"
                            placeholder="Street 1"
                            :error="editForm.errors.get('address.street1')"
                            @updated="editForm.errors.clear('address.street1')"
                            required
                        />
                        <app-input
                            v-model="editForm.address.street2"
                            placeholder="Street 2"
                            :error="editForm.errors.get('address.street2')"
                            @updated="editForm.errors.clear('address.street2')"
                        />
                        <app-input
                            v-model="editForm.address.street3"
                            placeholder="Street 3"
                            :error="editForm.errors.get('address.street3')"
                            @updated="editForm.errors.clear('address.street3')"
                        />
                        <app-input
                            v-model="editForm.address.city"
                            placeholder="City"
                            :error="editForm.errors.get('address.city')"
                            @updated="editForm.errors.clear('address.city')"
                            required
                        />
                        <app-input
                            v-model="editForm.address.postcode"
                            placeholder="Post Code"
                            :error="editForm.errors.get('address.postcode')"
                            @updated="editForm.errors.clear('address.postcode')"
                            required
                        />
                        <app-select
                            v-model="editForm.address.state"
                            placeholder="State"
                            :error="editForm.errors.get('address.state')"
                            @updated="editForm.errors.clear('address.state')"
                            :options="statesOptions[editForm.address.country]"
                            value-key="id"
                            label-key="label"
                            required
                        />
                        <app-select
                            v-model="editForm.address.country"
                            placeholder="Country"
                            :error="editForm.errors.get('address.country')"
                            @updated="(value) => {
                                editForm.errors.clear('address.country')

                                if (this.selected.Country !== value) {
                                    editForm.address.state = null;
                                }

                                editForm.address.iso2Code = iso2CodesOptions[value]
                            }"
                            :options="countriesOptions"
                            required
                        />
                        <checkbox
                            v-model="editForm.usePrimaryAddressAsBilling"
                        >Use this as the billing address</checkbox>
                    </div>
                </div>
                <div class="card mt-4" v-if="!editForm.usePrimaryAddressAsBilling">
                    <div class="card-header text-xs">Billing Address</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="editForm.billingAddress.street1"
                            placeholder="Street 1"
                            :error="editForm.errors.get('billingAddress.street1')"
                            @updated="editForm.errors.clear('billingAddress.street1')"
                            required
                        />
                        <app-input
                            v-model="editForm.billingAddress.street2"
                            placeholder="Street 2"
                            :error="editForm.errors.get('billingAddress.street2')"
                            @updated="editForm.errors.clear('billingAddress.street2')"
                        />
                        <app-input
                            v-model="editForm.billingAddress.street3"
                            placeholder="Street 3"
                            :error="editForm.errors.get('billingAddress.street3')"
                            @updated="editForm.errors.clear('billingAddress.street3')"
                        />
                        <app-input
                            v-model="editForm.billingAddress.city"
                            placeholder="City"
                            :error="editForm.errors.get('billingAddress.city')"
                            @updated="editForm.errors.clear('billingAddress.city')"
                            required
                        />
                        <app-input
                            v-model="editForm.billingAddress.postcode"
                            placeholder="Post Code"
                            :error="editForm.errors.get('billingAddress.postcode')"
                            @updated="editForm.errors.clear('billingAddress.postcode')"
                            required
                        />
                        <app-select
                            v-model="editForm.billingAddress.state"
                            placeholder="State"
                            :error="editForm.errors.get('billingAddress.state')"
                            @updated="editForm.errors.clear('billingAddress.state')"
                            :options="statesOptions[editForm.billingAddress.country]"
                            value-key="id"
                            label-key="label"
                            required
                        />
                        <app-select
                            v-model="editForm.billingAddress.country"
                            placeholder="Country"
                            :error="editForm.errors.get('billingAddress.country')"
                            @updated="(value) => {
                                editForm.errors.clear('billingAddress.country')

                                if (this.selected.BillingCountry !== value) {
                                    editForm.billingAddress.state = null;
                                }

                                editForm.billingAddress.iso2Code = iso2CodesOptions[value];
                            }"
                            :options="countriesOptions"
                            required
                        />
                    </div>
                </div>
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showEditModal = false"
                        :disable="updating"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="update"
                        :loading="updating"
                    >Update</app-button>
                </div>
            </template>
        </app-modal>
        <app-modal
            :show.sync="showAddCSPTenantModal"
            width="lg:w-1/3"
            bg="bg-gray-200"
        >
            <template slot="header">Add CSP Tenant</template>
            <template slot="body">
                <div class="card">
                    <div class="card-header text-xs">Tenant Details</div>
                    <div class="px-5 pb-5">
                        <app-input
                            v-model="addCSPTenantForm.endCustomerName"
                            placeholder="Customer Name"
                            disable
                        />
                        <app-input
                            v-model="addCSPTenantForm.defaultDomainPrefix"
                            placeholder="Microsoft Domain Prefix"
                            :error="addCSPTenantForm.errors.get('defaultDomainPrefix')"
                            @updated="defaultDomainPrefixUpdated"
                            append-text=".onmicrosoft.com"
                            focus
                            required
                            :loading="checkingDefaultDomainPrefix"
                            :checked="isDefaultDomainPrefixAvailable"
                        />
                        <app-select
                            v-model="addCSPTenantForm.microsoftMarketCategory"
                            placeholder="Tenant Microsoft Market Category"
                            :error="addCSPTenantForm.errors.get('microsoftMarketCategory')"
                            @updated="addCSPTenantForm.errors.clear('microsoftMarketCategory')"
                            :options="microsoftMarketCategories"
                            required
                        />
                    </div>
                </div>
                <div class="card mt-4">
                    <div class="card-header text-xs">Contact Details</div>
                    <div class="px-5 pb-5">
                        <div class="grid grid-cols-2 gap-4">
                            <app-input
                                v-model="addCSPTenantForm.contactFirstName"
                                placeholder="First Name"
                                :error="addCSPTenantForm.errors.get('contactFirstName')"
                                @updated="addCSPTenantForm.errors.clear('contactFirstName')"
                                required
                            />
                            <app-input
                                v-model="addCSPTenantForm.contactLastName"
                                placeholder="Last Name"
                                :error="addCSPTenantForm.errors.get('contactLastName')"
                                @updated="addCSPTenantForm.errors.clear('contactLastName')"
                                required
                            />
                        </div>
                        <app-input
                            v-model="addCSPTenantForm.contactEmailAddress"
                            placeholder="Email"
                            :error="addCSPTenantForm.errors.get('contactEmailAddress')"
                            @updated="addCSPTenantForm.errors.clear('contactEmailAddress')"
                            required
                        />
                        <app-input
                            v-model="addCSPTenantForm.contactPhoneNumber"
                            placeholder="Main Phone"
                            :error="addCSPTenantForm.errors.get('contactPhoneNumber')"
                            @updated="addCSPTenantForm.errors.clear('contactPhoneNumber')"
                            required
                        />
                    </div>
                </div>
                <div class="mt-5 flex items-center justify-end">
                    <app-button 
                        class="btn-white" 
                        @click="showAddCSPModal = false"
                        :disable="addingTenant"
                    >Cancel</app-button>
                    <app-button 
                        class="btn-blue ml-3" 
                        @click="addTenant"
                        :loading="addingTenant"
                    >Add Tenant</app-button>
                </div>
            </template>
        </app-modal>
    </div>
</template>

<script>
    import Http from '@/helpers/Http';
    import Form from '@/helpers/Form';
    import debounce from 'lodash.debounce';

    export default {
        data() {
            return {
                refreshTable: false,
                tableIsLoading: false,
                tableLoadingText: null,
                showAddModal: false,
                showEditModal: false,
                showAddCSPTenantModal: false,
                checkingDefaultDomainPrefix: false,
                isDefaultDomainPrefixAvailable: false,
                addingTenant: false,
                creating: false,
                updating: false,
                selected: {},
                countriesOptions: [
                    'Australia',
                    'United States of America',
                ],
                statesOptions: {
                    Australia: [
                        {id: 'NSW', label: 'New South Wales'},
                        {id: 'QLD', label: 'Queensland'},
                        {id: 'SA', label: 'South Australia'},
                        {id: 'Tas', label: 'Tasmania'},
                        {id: 'Vic', label: 'Victoria'},
                        {id: 'WA', label: 'Western Australia'},
                    ]
                },
                iso2CodesOptions: {
                    Australia: 'AU',
                    'United States of America': 'US',
                },
                microsoftMarketCategories: ['Corporate', 'Government', 'Academic'],
                addCSPTenantForm: new Form({
                    defaultDomainPrefix: null,
                    contractAgreementId: null,
                    endCustomerId: null,
                    endCustomerName: null,
                    contactFirstName: null,
                    contactLastName: null,
                    contactPhoneNumber: null,
                    contactEmailAddress: null,
                    microsoftMarketCategory: 'Corporate',
                }),
                addForm: new Form({
                    customerName: null,
                    webUrl: null,
                    email: null,
                    registrationNumber: null,
                    usePrimaryAddressAsBilling: true,
                    address: {
                        street1: null,
                        street2: null,
                        street3: null,
                        city: null,
                        state: null,
                        postcode: null,
                        country: 'Australia', 
                        iso2Code: 'AU',
                    },
                    billingAddress: {
                        street1: null,
                        street2: null,
                        street3: null,
                        city: null,
                        state: null,
                        postcode: null,
                        country: 'Australia', 
                        iso2Code: 'AU',
                    }
                }),
                editForm: new Form({
                    customerName: null,
                    webUrl: null,
                    email: null,
                    registrationNumber: null,
                    usePrimaryAddressAsBilling: true,
                    address: {
                        street1: null,
                        street2: null,
                        street3: null,
                        city: null,
                        state: null,
                        postcode: null,
                        country: 'Australia', 
                        iso2Code: 'AU',
                    },
                    billingAddress: {
                        street1: null,
                        street2: null,
                        street3: null,
                        city: null,
                        state: null,
                        postcode: null,
                        country: 'Australia', 
                        iso2Code: 'AU',
                    }
                }),
                columns: [
                    {
                        title: 'Name',
                        field: 'CustomerName',
                        sorter: 'string',
                        minWidth: 200,
                    },
                    {
                        title: 'Email',
                        field: 'Email',
                        sorter: 'string',
                        minWidth: 200,
                        formatter({_cell}) {
                            if (_cell.value) {
                                return _cell.value;
                            }

                            return '--';
                        }
                    },
                    {
                        title: 'Status', 
                        field: 'IsActive', 
                        minWidth: 130,
                        formatter({_cell}, params) {
                            if (_cell.value) {
                                return `<span class='badge badge-green'>Active</span>`;
                            }

                            return `<span class='badge badge-red'>Inactive</span>`;
                        }
                    },
                ],
                actions: [
                    {
                        label: 'Edit',
                        icon: () => import('@/icons/EditOutlined.vue'),
                        handler: customers => {
                            this.showEditModal = true;
                            this.selected = customers[0];
                            this.editForm.set({
                                customerName: this.selected.CustomerName,
                                webUrl: this.selected.WebUrl,
                                email: this.selected.Email,
                                registrationNumber: this.selected.RegistrationNumber,
                                usePrimaryAddressAsBilling: this.selected.Street1 == this.selected.BillingStreet1,
                                address: {
                                    street1: this.selected.Street1,
                                    street2: this.selected.Street2,
                                    street3: this.selected.Street3,
                                    city: this.selected.City,
                                    state: this.selected.State,
                                    postcode: this.selected.Postcode,
                                    country: this.selected.Country, 
                                    iso2Code: this.selected.CountryIsoCode,
                                },
                                billingAddress: {
                                    street1: this.selected.BillingStreet1,
                                    street2: this.selected.BillingStreet2,
                                    street3: this.selected.BillingStreet3,
                                    city: this.selected.BillingCity,
                                    state: this.selected.BillingState,
                                    postcode: this.selected.BillingPostcode,
                                    country: this.selected.BillingCountry, 
                                    iso2Code: this.selected.BillingCountryIsoCode,
                                }
                            });
                        }
                    },
                    {
                        label: 'Add CSP Tenant',
                        icon: () => import('@/icons/PersonAddOutlined.vue'),
                        handler: customers => {
                            this.addCSPTenantForm.errors.clear();
                            this.addCSPTenantForm.reset();
                            this.addCSPTenantForm.set({
                                endCustomerId: customers[0].CustomerId,
                                endCustomerName: customers[0].CustomerName,
                            });
                            this.showAddCSPTenantModal = true;
                        }
                    },
                    {
                        label: 'Activate',
                        icon: () => import('@/icons/VisibilityOutlined.vue'),
                        requiredSelect: true,
                        handler: customers => {
                            this.activate(customers[0]);
                        },
                        isEligible(customers) {
                            return !customers[0].IsActive;
                        }
                    },
                    {
                        label: 'Deactivate',
                        icon: () => import('@/icons/VisibilityOffOutlined.vue'),
                        requiredSelect: true,
                        handler: customers => {
                            this.deactivate(customers[0]);
                        },
                        isEligible(customers) {
                            return customers[0].IsActive;
                        }
                    },
                    {
                        label: 'Sync',
                        icon: () => import('@/icons/Sync.vue'),
                        handler: this.sync
                    },
                    {
                        label: 'Add',
                        icon: () => import('@/icons/Add.vue'),
                        handler: () => {
                            this.addForm.errors.clear();
                            this.showAddModal = true;
                        },
                    },
                ],
            }
        },
        methods: {
            sync() {
                this.tableIsLoading = true;
                this.tableLoadingText = 'Syncing...';
                Http.patch('rhipe/customers/sync')
                    .then(() => {
                        this.tableIsLoading = false;
                        this.refreshTable = true;
                    })
                    .catch(() => {
                        this.tableIsLoading = false;
                    });
            },
            create() {
                this.creating = true;
                this.addForm.errors.clear();
                Http.post('rhipe/customers', this.addForm.payload())
                    .then(({data}) => {
                        this.creating = false;
                        this.showAddModal = false;
                        this.sync();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Customer was successfully created.'
                        })
                    })
                    .catch(({response}) => {
                        this.creating = false;
                        this.addForm.errors.record(response.data);
                    });
            },
            update() {
                this.updating = true;
                this.editForm.errors.clear();
                Http.patch(`rhipe/customers/${this.selected.CustomerId}`, this.editForm.payload())
                    .then(({data}) => {
                        this.updating = false;
                        this.showEditModal = false;
                        this.sync();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Customer was successfully updated.'
                        })
                    })
                    .catch(({response}) => {
                        this.updating = false;
                        this.editForm.errors.record(response.data);
                    });
            },
            activate(customer) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to activate "<strong>${customer.CustomerName}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, activate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`rhipe/customers/${customer.CustomerId}/activate`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.sync();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Customer was successfully activated.'
                        })
                    }
                })
            },
            deactivate(customer) {
                this.$swal.fire({
                    type: 'warning',
                    title: 'Are you sure?',
                    html: `You are about to deactivate "<strong>${customer.CustomerName}</strong>" account.`,
                    showCancelButton: true,
                    confirmButtonColor: '#f56565',
                    confirmButtonText: 'Yes, deactivate it!',
                    showLoaderOnConfirm: true,
                    preConfirm: (response) => {
                        return Http.patch(`rhipe/customers/${customer.CustomerId}/deactivate`);
                    },
                    allowOutsideClick: () => !this.$swal.isLoading()
                })
                .then((result) => {
                    if (result.value) {
                        this.sync();
                        this.$swal.fire({
                            type: 'success',
                            title: 'Success!',
                            text: 'Customer was successfully deactivated.'
                        })
                    }
                })
            },
            defaultDomainPrefixUpdated: debounce(function (value) {
                this.addCSPTenantForm.errors.clear('defaultDomainPrefix');
                this.checkingDefaultDomainPrefix = true;
                this.isDefaultDomainPrefixAvailable = false;
                Http.post('rhipe/microsoftcsp/tenants/checkDefaultDomainPrefixAvailability', {defaultDomainPrefix: value})
                    .then(({ data }) => {
                        this.checkingDefaultDomainPrefix = false;
                        this.isDefaultDomainPrefixAvailable = true;
                    })
                    .catch(({ response }) => {
                        this.checkingDefaultDomainPrefix = false;
                        this.addCSPTenantForm.errors.record(response.data);
                    })               
            }, 1000),
            addTenant() {
                this.addingTenant = true;
                this.addCSPTenantForm.errors.clear();
                Http.post('rhipe/microsoftcsp/tenants', this.addCSPTenantForm.payload())
                    .then(({ data }) => {
                        this.addingTenant = false;
                    })
                    .catch(({ response }) => {
                        this.addingTenant = false;
                        this.addCSPTenantForm.errors.record(response.data);
                    })
            }
        }
    }
</script>